// RITA-1535 — Platzhalterkatalog des Vertragseditors.
//
// Der Vertragseditor zeigt alle Platzhalter des gemeinsamen Katalogs (`features/templates/catalog.ts`,
// geteilt mit Briefvorlagen und Systemzugangs-Mails) und zusätzlich eigene Platzhalter für die
// Anschreiben zum Vertrag, die es nur hier gibt: Die Persönliche Anrede („Liebe Anna“ / „Lieber Max“)
// steht in „Persönliche Daten“ direkt hinter ANREDE; der Bearbeiter (Vor- und Nachname der angemeldeten
// Mitarbeiterin aus Einstellungen › Benutzer) und das Heutige Datum (Druckdatum, z. B. „08. Oktober 2026“)
// stehen in der eigenen Gruppe „Allgemein“ ganz unten. Briefvorlagen und Mails bleiben beim gemeinsamen
// Katalog und kennen diese Schlüssel nicht (Entscheidung der Auftraggeberin, 08.10.2026). Mit echten
// Daten gefüllt werden sie erst bei der Vertragserstellung beim Berater; hier gibt es feste Beispielwerte,
// die zur Beispielperson Max Mustermann (ANREDE „Herr“) passen. RITA-1560 ergänzt VERTRAGSPARTNER
// unmittelbar vor VERTRAGSINHABER; der gemeinsame Katalog und gespeicherte Vorlagen bleiben unverändert.
// RITA-1597: Die Beispielwerte zeigen, was später wirklich im Vertrag steht: FIRMA ist unsere Marke (die
// Marke aus der Zuordnung der Vorlage, sonst `BEISPIEL_MARKE`), VERTRAGSINHABER gehört zum Berater und
// ist im Grundfall „Berater“ leer – wie bei den meisten Beratern; VERTRAGSPARTNER ist dann der Berater.
// Weitere Beispielfälle (Beraterin, Vertragsinhaberin, Firma) bauen in `beispielfaelle.ts` darauf auf.
import { PLACEHOLDER_CATEGORIES, PLACEHOLDER_EXAMPLE_VALUES, PlaceholderKey } from '../../catalog';

/** Platzhalter, die es nur im Vertragseditor gibt (Reihenfolge wie in der Leiste). */
export const VERTRAG_EIGENE_PLATZHALTER = [
  'PERSOENLICHE_ANREDE',
  'VERTRAGSPARTNER',
  'BEARBEITER',
  'HEUTIGES_DATUM',
] as const;

export type VertragEigenerPlatzhalter = (typeof VERTRAG_EIGENE_PLATZHALTER)[number];

/** Schlüssel eines Platzhalters im Vertragsdokument: gemeinsamer Katalog plus eigene Platzhalter. */
export type VertragPlatzhalterKey = PlaceholderKey | VertragEigenerPlatzhalter;

export interface VertragPlatzhaltergruppe {
  label: string;
  platzhalter: readonly VertragPlatzhalterKey[];
}

function hinterEinfuegen(
  liste: readonly VertragPlatzhalterKey[],
  nach: VertragPlatzhalterKey,
  neu: VertragPlatzhalterKey,
): VertragPlatzhalterKey[] {
  const index = liste.indexOf(nach);
  if (index < 0) throw new Error(`Platzhalter ${nach} fehlt im gemeinsamen Katalog`);
  return [...liste.slice(0, index + 1), neu, ...liste.slice(index + 1)];
}

/** Name der eigenen Gruppe am Ende der Leiste (Entscheidung der Auftraggeberin, 08.10.2026). */
export const VERTRAG_GRUPPE_ALLGEMEIN = 'Allgemein';

function gruppenPlatzhalter(gruppe: {
  label: string;
  placeholders: readonly PlaceholderKey[];
}): readonly VertragPlatzhalterKey[] {
  if (gruppe.label === 'Persönliche Daten') {
    return hinterEinfuegen(gruppe.placeholders, 'ANREDE', 'PERSOENLICHE_ANREDE');
  }
  if (gruppe.label === 'Vertrag & Firma') {
    return gruppe.placeholders.flatMap((schluessel): VertragPlatzhalterKey[] =>
      schluessel === 'VERTRAGSINHABER' ? ['VERTRAGSPARTNER', schluessel] : [schluessel],
    );
  }
  return gruppe.placeholders;
}

/**
 * Gruppen der Platzhalterleiste: die gemeinsamen Gruppen (PERSOENLICHE_ANREDE direkt hinter ANREDE) und ganz
 * unten die Gruppe „Allgemein“.
 */
export const VERTRAG_PLATZHALTER_GRUPPEN: readonly VertragPlatzhaltergruppe[] = [
  ...PLACEHOLDER_CATEGORIES.map((gruppe) => ({
    label: gruppe.label,
    platzhalter: gruppenPlatzhalter(gruppe),
  })),
  { label: VERTRAG_GRUPPE_ALLGEMEIN, platzhalter: ['BEARBEITER', 'HEUTIGES_DATUM'] },
];

/** Alle Schlüssel, die ein Vertragsdokument enthalten darf, in der Reihenfolge der Leiste. */
export const BEKANNTE_VERTRAG_PLATZHALTER: readonly VertragPlatzhalterKey[] = VERTRAG_PLATZHALTER_GRUPPEN.flatMap(
  (gruppe) => [...gruppe.platzhalter],
);

export function istVertragPlatzhalter(schluessel: string): schluessel is VertragPlatzhalterKey {
  return (BEKANNTE_VERTRAG_PLATZHALTER as readonly string[]).includes(schluessel);
}

/** RITA-1597: Marke für {{FIRMA}}, solange die Vorlage keiner Marke zugeordnet ist (erste Marke im PRM). */
export const BEISPIEL_MARKE = 'Mein Urlaubsglück GmbH';

/**
 * Beispielwerte der Vorschau im Grundfall „Berater“ (Herr Max Mustermann ohne abweichenden Vertragsinhaber);
 * die eigenen Werte sind fest (Entscheidung der Auftraggeberin, 08.10.2026).
 */
export const VERTRAG_BEISPIELWERTE: Readonly<Record<VertragPlatzhalterKey, string>> = {
  ...PLACEHOLDER_EXAMPLE_VALUES,
  FIRMA: BEISPIEL_MARKE,
  VERTRAGSINHABER: '',
  PERSOENLICHE_ANREDE: 'Lieber Max',
  VERTRAGSPARTNER: 'Herr Max Mustermann',
  BEARBEITER: 'Erika Musterfrau',
  HEUTIGES_DATUM: '08. Oktober 2026',
};
